import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellRing, X, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * LogReminderControl — a bell button + inline popover that lets a member
 * attach a reminder to any tracking log entry (procedure, fitness session,
 * peptide dose, skincare, habit, or custom). Backed by /api/tracking-
 * reminders. When one or more reminders exist for this log, the bell
 * turns terracotta and shows the count.
 *
 * Props:
 *   room       — one of: procedure | fitness | peptide | skincare | habit | custom
 *   refId      — id of the log entry, or a stable freeform key
 *   refLabel   — short label shown in reminder toasts (e.g. "Retinol tonight")
 *   size       — icon size, defaults to 14
 *   testid     — data-testid prefix for the bell/panel/etc.
 */
export default function LogReminderControl({
  room,
  refId,
  refLabel = "",
  size = 14,
  testid = "log-reminder",
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null); // {top,left} in viewport coords
  const load = async () => {
    try {
      const { data } = await endpoints.listTrackingReminders({ room, ref_id: refId });
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [room, refId]);

  // Anchor the portal-rendered panel next to the button, and keep it there
  // if the page scrolls or resizes while the popover is open.
  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 8, left: Math.max(8, r.right - 320) });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const count = items.length;
  const has = count > 0;

  return (
    <div className="relative inline-flex" data-testid={`${testid}-root`}>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={has ? `${count} reminder${count > 1 ? "s" : ""}` : "Add reminder"}
        data-testid={`${testid}-toggle`}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.2em] border hairline tab-link ${
          has
            ? "bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] border-[hsl(var(--terracotta))]/30"
            : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sand))]"
        }`}
      >
        {has ? <BellRing size={size} /> : <Bell size={size} />}
        {has ? count : "Remind"}
      </button>

      {open && anchor && createPortal(
        <ReminderPanel
          room={room}
          refId={refId}
          refLabel={refLabel}
          items={items}
          loading={loading}
          onClose={() => setOpen(false)}
          onChanged={load}
          setLoading={setLoading}
          testid={testid}
          top={anchor.top}
          left={anchor.left}
        />,
        document.body,
      )}
    </div>
  );
}

function ReminderPanel({ room, refId, refLabel, items, onClose, onChanged, testid, top, left }) {
  // Default remind_at to tomorrow morning at 9:00 in the member's local time.
  const defaultWhen = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocal(d);
  }, []);
  const [when, setWhen] = useState(defaultWhen);
  const [cadence, setCadence] = useState("once");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!when) { toast.error("Choose a date and time."); return; }
    setSaving(true);
    try {
      await endpoints.createTrackingReminder({
        room, ref_id: refId, ref_label: refLabel,
        remind_at: when, cadence, note: note.trim(),
      });
      setNote("");
      toast.success("Reminder set.");
      onChanged?.();
    } catch {
      toast.error("Couldn't save just yet.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await endpoints.deleteTrackingReminder(id);
      toast.success("Reminder removed.");
      onChanged?.();
    } catch {
      toast.error("Couldn't remove.");
    }
  };

  return (
    <div
      style={{ position: "fixed", top, left, width: 320, zIndex: 200 }}
      className="p-4 border hairline rounded-md bg-[hsl(var(--paper))] shadow-lg"
      data-testid={`${testid}-panel`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
          Reminder
          {refLabel && <span className="ml-2 text-[hsl(var(--muted-foreground))] normal-case tracking-normal">· {refLabel}</span>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="p-1 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sand))]">
          <X size={14} />
        </button>
      </div>

      {items.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testid}-list`}>
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 p-2 rounded bg-[hsl(var(--sand))]/40 text-sm">
              <div className="min-w-0">
                <div className="truncate">{formatWhen(it.remind_at)}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                  {it.cadence}{it.note ? ` · ${it.note}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(it.id)}
                aria-label="Remove reminder"
                data-testid={`${testid}-remove-${it.id}`}
                className="p-1.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 space-y-2">
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
          Date &amp; time
          <Input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="mt-1"
            data-testid={`${testid}-when`}
          />
        </label>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
          Cadence
          <Select value={cadence} onValueChange={setCadence}>
            <SelectTrigger className="mt-1" data-testid={`${testid}-cadence`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="once">Once</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
          Note (optional)
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A small nudge for later…"
            className="mt-1 text-sm"
            data-testid={`${testid}-note`}
          />
        </label>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          onClick={save}
          disabled={saving || !when}
          data-testid={`${testid}-save`}
          className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
        >
          <Check size={13} className="mr-1" /> {saving ? "Saving…" : "Set reminder"}
        </Button>
      </div>
    </div>
  );
}

/** Format a Date into the datetime-local input value ("YYYY-MM-DDTHH:MM"). */
function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format an ISO reminder timestamp for display. */
function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
